import React, { useEffect, useState } from "react";
import { api, downloadAuthed } from "@/lib/api";
import { inr0, fmtDate } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge, Stat } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { AlertTriangle, FileSpreadsheet } from "lucide-react";

const FILTERS = ["all", "overdue", "partially_paid", "due_soon", "long_outstanding"];

export default function Outstanding() {
  const [data, setData] = useState({ buckets: {}, rows: [] });
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/reports/aging").then(setData).catch(() => {}); }, []);
  const rows = data.rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "overdue") return r.days_overdue > 0;
    if (filter === "partially_paid") return r.status === "partially_paid";
    if (filter === "due_soon") return r.days_overdue <= 0;
    if (filter === "long_outstanding") return r.days_overdue > 60;
    return true;
  });
  const b = data.buckets;
  return (
    <div>
      <PageHeader title="Outstanding Rent" subtitle="Accounts receivable & aging" testid="outstanding-header">
        <Button variant="outline" onClick={() => downloadAuthed("/reports/aging/export", "aging.xlsx")}><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Stat label="Current" value={inr0(b.current)} tone="success" />
        <Stat label="1–30 days" value={inr0(b["1_30"])} tone="warning" />
        <Stat label="31–60 days" value={inr0(b["31_60"])} tone="warning" />
        <Stat label="61–90 days" value={inr0(b["61_90"])} tone="danger" />
        <Stat label="90+ days" value={inr0(b["90_plus"])} tone="danger" />
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map((f) => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${filter === f ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{f.replace(/_/g, " ")}</button>)}
      </div>
      {rows.length === 0 ? <EmptyState icon={AlertTriangle} title="No outstanding rent" hint="All invoices are settled." /> : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Invoice</TableHead><TableHead>Tenant / Property</TableHead><TableHead>Due Date</TableHead><TableHead className="text-right">Invoice</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-center">Days Overdue</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} className={`hover:bg-slate-50/50 ${r.days_overdue > 60 ? "bg-rose-50/40" : ""}`}>
                  <TableCell className="font-mono text-xs">{r.invoice_no}</TableCell>
                  <TableCell><div className="font-medium text-slate-800">{r.tenant}</div><div className="text-xs text-slate-400">{r.property}</div></TableCell>
                  <TableCell className="text-xs">{fmtDate(r.due_date)}</TableCell>
                  <TableCell className="text-right">{inr0(r.total)}</TableCell>
                  <TableCell className="text-right text-emerald-600">{inr0(r.paid)}</TableCell>
                  <TableCell className="text-right font-semibold text-rose-600">{inr0(r.balance)}</TableCell>
                  <TableCell className="text-center">{r.days_overdue > 0 ? <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-700 text-xs font-medium">{r.days_overdue}d</span> : <span className="text-slate-400 text-xs">—</span>}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
